import { useField, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import FileIcon from '../Icons/FileIcon';
import { FieldErrorInfo } from './FormField';

interface CustomFileUploaderProps {
  label: string;
  name: string;
  className?: string;
  children?: any;
  noUpload?: boolean;
  modal?: boolean;
}

const CustomFileUploader: React.FC<CustomFileUploaderProps> = ({
  label,
  name,
  className,
  noUpload = false, // default value
  modal,
}) => {
  const [field, meta, helpers] = useField(name);
  const [imageName, setImageName] = useState<string>('');
  const { handleBlur } = useFormikContext<any>();

  useEffect(() => {
    if (field.value) {
      const file = field.value as File;
      if (typeof file === 'string') {
        setImageName(file);
      } else {
        setImageName(file.name);
      }
    } else {
      setImageName('');
    }
    // eslint-disable-next-line
  }, [field.value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // if noUpload is true, simply do nothing
    if (noUpload) return;

    const file = event.target.files && event.target.files[0];
    if (file) {
      helpers.setValue(file);
    }
  };

  return (
    <InputWrapper $noUpload={noUpload} className={className}>
      <InputLabel htmlFor={name}>
        {label}
        <InputContainer $noUpload={noUpload}>
          <FileInputField
            id={name}
            name={name}
            type="file"
            accept=".pdf, image/*"
            onChange={handleChange}
            disabled={noUpload} // disable native input if noUpload is true
            placeholder="dddd"
          />

          <IconStyle>
            <FileIcon />
          </IconStyle>

          {modal && !imageName && <Modaltext>click here to select</Modaltext>}

          <PseudoFormField
            tabIndex={0}
            onClick={() => {
              if (!noUpload) {
                handleBlur({ target: { name } });
              }
            }}
          >
            {imageName}
          </PseudoFormField>
        </InputContainer>
      </InputLabel>

      {meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default CustomFileUploader;

const InputWrapper = styled.div<{ $noUpload?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* When noUpload is true, gray out the wrapper and disable pointer events */
  ${(props) =>
    props.$noUpload &&
    `
      background-color: ${'none'};
      cursor: not-allowed;
    `}
`;

const InputContainer = styled.div<{ $noUpload?: boolean }>`
  display: grid;
  position: relative;
  border: 1px solid ${({ theme }) => theme.palette.greyGrey3};
  border-radius: 4px;
  height: 56px;

  & > * {
    grid-column: 1;
    grid-row: 1;
  }

  &:focus-within {
    outline: none;
    border-color: ${({ theme }) => theme.palette.greyGrey1};
  }

  &:hover {
    cursor: ${(props) =>
      props.$noUpload
        ? 'not-allowed'
        : "url('/static/svg/file-cursor.svg'), copy"};
  }

  /* When disabled, apply a grayed-out background and block pointer events */
  ${(props) =>
    props.$noUpload &&
    `
      background-color: ${'#f5f5f5'};
      pointer-events: none;
  `}
`;

const FileInputField = styled.input`
  display: none;

  &:disabled {
    cursor: none;
  }
`;

const PseudoFormField = styled.div`
  text-align: left;
  font-weight: 300;
  color: ${({ theme }) => theme.palette.blackBlack2};
  font-size: 16px;
  cursor: text;
  overflow: auto;
  display: grid;
  width: calc(100% - 64px);
  margin: 12px;

  /* hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  &:hover {
    cursor: url('/static/svg/file-cursor.svg'), copy;
  }
`;

const InputLabel = styled.label`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-size: 18px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.blackBlack2};
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const IconStyle = styled.div`
  position: absolute;
  top: 17px;
  right: 18px;
  z-index: 1;
  background-color: white;

  @media (max-width: 480px) {
    top: 9px;
    right: 10px;
  }
`;

const Modaltext = styled.p`
  color: ${({ theme }) => theme.palette.greyGrey2};
  margin-top: auto;
  margin-bottom: auto;
  margin-left: 1rem;
`;
